/**
 * BaseUnit.js
 * PSP/CRE Tool v2.0 - Hierarchical Force Structure
 * 
 * Base class for all military units (Battalion, Brigade, Division, Corps)
 * Provides common functionality for hierarchy management, strength tracking,
 * and casualty application.
 * 
 * Author: Dr. Tatar Radu MD
 * Date: November 26, 2025
 * Version: 1.1 (February 14, 2026)
 * 
 * CHANGELOG v1.1 (February 14, 2026):
 * - CRITICAL FIX: Added breakdown property to constructor and toJSON()
 * - ForceService v3.0.6 passes breakdown {command, support} in config
 * - BaseUnit now stores and serializes this field for all unit types
 * - Fixes [BUG-065]: Missing breakdown data in calculator
 * - Lines changed: Constructor (~37), toJSON() (~256)
 */

export class BaseUnit {
  /**
   * Create a military unit
   * 
   * @param {Object} config - Unit configuration
   * @param {string} config.id - Unique identifier for this unit
   * @param {string} config.name - Human-readable name (e.g., "1st Battalion")
   * @param {string} config.type - Unit type: 'BATTALION', 'BRIGADE', 'DIVISION', 'CORPS'
   * @param {number} [config.strength=0] - Personnel strength (number of soldiers)
   * @param {number} [config.position_feba_km=0] - Distance from FEBA in kilometers
   */
  constructor(config) {
    // Unique identifier (e.g., 'bn1', 'brig2', 'div1')
    this.id = config.id;
    
    // Human-readable name
    this.name = config.name;
    
    // Unit type for classification
    this.type = config.type; // 'BATTALION', 'BRIGADE', 'DIVISION', 'CORPS'

    // National standard (Romanian, NATO, US, or MULTINATIONAL)
    this.nationalStandard = config.nationalStandard || 'NATO';  // Default to NATO if not specified
    
    // Personnel strength
    this.strength = config.strength || 0;           // Initial strength
    this.current_strength = this.strength;          // Current strength (decreases with casualties)
    
    // Personnel breakdown (command vs support) - added by ForceService v3.0.6
    this.breakdown = config.breakdown || null;      // { command: N, support: N } or null
    
    // Hierarchy relationships
    this.parent = null;                             // Parent unit (set when added to parent)
    this.children = [];                             // Child units (battalions, brigades, etc.)
    
    // Tactical positioning
    this.position_feba_km = config.position_feba_km || 0;  // Distance from Forward Edge of Battle Area
    
    // Metadata
    this.created_at = new Date();
  }
  
  // ============================================================================
  // HIERARCHY MANAGEMENT METHODS
  // ============================================================================
  
  /**
   * Set parent unit in hierarchy
   * 
   * @param {BaseUnit} parent - Parent unit
   */
  setParent(parent) {
    this.parent = parent;
  }
  
  /**
   * Add child unit to this unit
   * 
   * @param {BaseUnit} child - Child unit to add
   */
  addChild(child) {
    this.children.push(child);
    child.setParent(this);
  }
  
  /**
   * Get all ancestors of this unit (parent, grandparent, etc.)
   * 
   * @returns {BaseUnit[]} Array of ancestor units, ordered from immediate parent to root
   */
  getAncestors() {
    const ancestors = [];
    let current = this.parent;
    
    while (current) {
      ancestors.push(current);
      current = current.parent;
    }
    
    return ancestors;
  }
  
  /**
   * Get all descendants of this unit (children, grandchildren, etc.)
   * 
   * @returns {BaseUnit[]} Array of all descendant units
   */
  getDescendants() {
    const descendants = [];
    
    for (const child of this.children) {
      descendants.push(child);
      descendants.push(...child.getDescendants());
    }
    
    return descendants;
  }
  
  /**
   * Get full hierarchical path as IDs
   * 
   * @returns {string} Path like "corps1/div2/brig1/bn3"
   */
  getFullPath() {
    const ancestors = this.getAncestors().reverse();
    const path = [...ancestors.map(a => a.id), this.id];
    return path.join('/');
  }
  
  /**
   * Get full hierarchical path as human-readable names
   * 
   * @returns {string} Path like "I Corps → Division 2 → Brigade 1 → Battalion 3"
   */
  getHumanReadablePath() {
    const ancestors = this.getAncestors().reverse();
    const path = [...ancestors.map(a => a.name), this.name];
    return path.join(' → ');
  }
  
  /**
   * Check if this unit is a descendant of another unit
   * 
   * @param {BaseUnit} unit - Potential ancestor unit
   * @returns {boolean} True if this unit is a descendant of the given unit
   */
  isDescendantOf(unit) {
    const ancestors = this.getAncestors();
    return ancestors.includes(unit);
  }
  
  // ============================================================================
  // STRENGTH TRACKING METHODS
  // ============================================================================
  
  /**
   * Apply casualties to this unit
   * Reduces current strength, prevents going below zero
   * 
   * @param {number} casualties - Number of casualties to apply
   * @returns {Object} Result object with casualties applied and overflow
   */
  applyCasualties(casualties) {
    const before = this.current_strength;
    this.current_strength -= casualties;
    
    if (this.current_strength < 0) {
      const overflow = Math.abs(this.current_strength);
      this.current_strength = 0;
      
      return {
        applied: before,
        overflow: overflow,
        unit_destroyed: true
      };
    }
    
    return {
      applied: casualties,
      overflow: 0,
      unit_destroyed: this.current_strength === 0
    };
  }
  
  /**
   * Get current strength as percentage of original
   * 
   * @returns {number} Percentage (0-100)
   */
  getStrengthPercentage() {
    if (this.strength === 0) return 0;
    return (this.current_strength / this.strength) * 100;
  }
  
  /**
   * Check if unit is combat effective
   * NATO standard: Unit is combat ineffective below 70% strength
   * 
   * @returns {boolean} True if unit is combat effective
   */
  isCombatEffective() {
    return this.getStrengthPercentage() >= 70;
  }
  
  /**
   * Get combat effectiveness rating
   * 
   * @returns {string} Rating: 'FULL', 'EFFECTIVE', 'DEGRADED', 'INEFFECTIVE', 'DESTROYED'
   */
  getCombatEffectivenessRating() {
    const percentage = this.getStrengthPercentage();
    
    if (percentage >= 95) return 'FULL';
    if (percentage >= 70) return 'EFFECTIVE';
    if (percentage >= 50) return 'DEGRADED';
    if (percentage > 0) return 'INEFFECTIVE';
    return 'DESTROYED';
  }
  
  /**
   * Reset strength to original (for scenario reset)
   */
  resetStrength() {
    this.current_strength = this.strength;
  }
  
  // ============================================================================
  // UTILITY METHODS
  // ============================================================================
  
  /**
   * Get summary information about this unit
   * 
   * @returns {Object} Summary object
   */
  getSummary() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      strength: this.strength,
      current_strength: this.current_strength,
      strength_percentage: this.getStrengthPercentage(),
      combat_effectiveness: this.getCombatEffectivenessRating(),
      position_feba_km: this.position_feba_km,
      has_parent: this.parent !== null,
      children_count: this.children.length,
      hierarchical_path: this.getHumanReadablePath()
    };
  }
  
  /**
   * Convert to JSON for storage/transfer
   * 
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      nationalStandard: this.nationalStandard,
      strength: this.strength,
      current_strength: this.current_strength,
      breakdown: this.breakdown,  // v3.0.6: Include personnel breakdown (command/support)
      position_feba_km: this.position_feba_km,
      created_at: this.created_at.toISOString()
    };
  }
  
  /**
   * Clone this unit (shallow copy, no children/parent)
   * 
   * @returns {BaseUnit} New unit with same properties
   */
  clone() {
    return new BaseUnit({
      id: this.id + '_clone',
      name: this.name + ' (Clone)',
      type: this.type,
      strength: this.strength,
      position_feba_km: this.position_feba_km
    });
  }
}

/**
 * USAGE EXAMPLES:
 * 
 * // Create a unit
 * const battalion = new BaseUnit({
 *   id: 'bn1',
 *   name: '1st Battalion',
 *   type: 'BATTALION',
 *   strength: 800,
 *   position_feba_km: 8
 * });
 * 
 * // Apply casualties
 * battalion.applyCasualties(50);
 * console.log(battalion.getStrengthPercentage()); // 93.75%
 * 
 * // Check effectiveness
 * console.log(battalion.isCombatEffective()); // true
 * console.log(battalion.getCombatEffectivenessRating()); // 'EFFECTIVE'
 * 
 * // Build hierarchy
 * const brigade = new BaseUnit({
 *   id: 'brig1',
 *   name: 'Brigade 1',
 *   type: 'BRIGADE',
 *   strength: 4000
 * });
 * 
 * brigade.addChild(battalion);
 * console.log(battalion.getHumanReadablePath()); // "Brigade 1 → 1st Battalion"
 */
